import InnerHTML from 'dangerously-set-html-content';
import React, {useEffect, useRef, useState} from "react";
import {Button, Card} from "react-bootstrap";
import {BiExpand} from "react-icons/bi";
import {useListContext} from "../../contexts/ListContext";
import {useFigure} from "../../hooks/useJsonLoader";
import {DataType, FigureType} from "../../types/enums";
import {Correlation, FigureResult, ListFigureData, Score} from "../../types/list";
import {applyHighlight, HIGHLIGHT_FIGURE_TYPES} from "../../utils/plotHighlight";


const ListFigure = ({type}: { type: FigureType }) => {
  const {listState, setShowFigureModal} = useListContext();

  const [figureData, setFigureData] = useState<ListFigureData | null>(null);
  const [figureResult, setFigureResult] = useState<FigureResult | undefined>(undefined);
  const figureContainerRef = useRef<HTMLDivElement>(null);
  const requestIdRef = useRef(0);

  const figure = useFigure(type);

  useEffect(() => {
    const container = figureContainerRef.current;
    if (!listState.selectedRowModal || !container) {
      setFigureData(null);
      setFigureResult(undefined);
      return;
    }

    const requestId = ++requestIdRef.current;
    const previewHeight = Math.max(container.clientHeight || 0, 10);

    const commonData = {
      window: 'preview',
      height: previewHeight
    }
    let data: ListFigureData;

    if (listState.dataType === DataType.Correlations) {
      const row = listState.selectedRowModal as Correlation;

      switch (type) {
        case FigureType.CorrelationDecomp:
          data = {
            ...commonData,
            gene1: row.g1_lid,
            gene2: row.g2_lid,
            pcc: row.pcc,
          }
          break;
        case FigureType.CorrelationSubnetworkG1:
          data = {
            ...commonData,
            library: row.g1_lid,
          }
          break;
        case FigureType.CorrelationSubnetworkG2:
          data = {
            ...commonData,
            library: row.g2_lid,
          }
          break;
        default:
          return;  // Should never happen
      }
    } else {
      const row = listState.selectedRowModal as Score;

      switch (type) {
        case FigureType.ScoreAll:
        case FigureType.ScoreQuery:
          data = {
            ...commonData,
            query: +row.screen_entrez.split(",")[0], // Default to first entrez ID if multiple are present
            screen_id: row.screen.split(/\s+/)[1],
            library: row.library_id,
          }
          break;
        case FigureType.ScoreLibrary:
          data = {
            ...commonData,
            library: row.library_id
          }
          break;
        default:
          return;  // Should never happen
      }
    }

    setFigureData(data);

    figure(data, false).then(result => {
      if (requestId !== requestIdRef.current) return; // Drop stale responses
      setFigureResult(result);
    });

    return () => {
      requestIdRef.current += 1;
      setFigureData(null);
      setFigureResult(undefined);
    }
  }, [figure, listState.dataType, listState.selectedRowModal]);

  useEffect(() => {
    if (!HIGHLIGHT_FIGURE_TYPES.includes(type) || !figureResult) return;

    const row = listState.dataType === DataType.Correlations
      ? listState.selectedRowModal as Correlation | null
      : listState.selectedRowModal as Score | null;

    return applyHighlight(figureContainerRef.current, type, row);
  }, [type, figureResult, listState.selectedRowModal]);

  const openModal = () => {
    if (!figureData) return;

    let data = {...figureData, window: "modal"};
    delete data.height;

    figure(data).then(result => {
      if (result) setShowFigureModal({...result, figure_type: type});
    });
  };

  return <Card className="h-100">
    <Card.Body className="d-flex flex-column">
      <div className="flex-grow-1 d-flex justify-content-center align-items-center figure-container"
           ref={figureContainerRef}>
        {!figureResult ? <>
          {listState.selectedRowModal
            ? <div className="spinner-border" role="status"/>
            : <div className="text-center text-muted">No data</div>}
        </> : <>
          <div className="d-block w-100 h-100"> {/* Figure requires block display for proper sizing */}
            <InnerHTML key={JSON.stringify(figureData)} html={figureResult.html}/>
          </div>
        </>}
      </div>
      <div className="d-flex justify-content-start mt-2">
        <Button variant="outline-dark" className="d-inline-flex flex-shrink-0 p-2" onClick={openModal}>
          <BiExpand className="ui-icon"/>
        </Button>
      </div>
    </Card.Body>
  </Card>;
}

export default ListFigure;
